import { Button, ButtonList, Position, Size, Tray } from '@busybusy/webapp-react-ui';
import { ColumnIcon } from 'assets/icons';
import classNames from 'classnames';
import { Header, IconButton, MoreButton } from 'components';
import { IActionHeaderTabData } from 'components/foundation/ActionHeaderTabBar/ActionHeaderTabBar';
import TabBar from 'components/foundation/TabBar/TabBar';
import { useQueryParams } from 'hooks';
import { t } from 'i18next';
import { ClassName } from 'types/ClassName';
import { QueryParam } from 'utils/constants/queryParam';
import CertificationStatusActionBar from '../CertificationStatusActionBar/CertificationStatusActionBar';
import { useCanManageCertifications } from '../hooks/useCanManageCertifications';

export interface ICertificationStatusReportTabHeaderProps {
  className?: ClassName;
  selectedTab: CertificationStatusReportTabType;
  onTabSelect: (tab: CertificationStatusReportTabType) => void;
  onNewRecordClick: () => void;
  handleColumnButtonClick: () => void;
  handleManageCertificationsButtonClick: () => void;
  checkedIds: string[];
  onDelete: () => void;
  onClear: () => void;
  onPrint: () => void;
  onExport: () => void;
}

export enum CertificationStatusReportTabType {
  ACTIVE = 'active',
  EXPIRED = 'expired',
}

const CertificationStatusReportTabHeader = (props: ICertificationStatusReportTabHeaderProps) => {
  const {
    className,
    selectedTab,
    onTabSelect,
    onNewRecordClick,
    handleColumnButtonClick,
    handleManageCertificationsButtonClick,
    checkedIds,
    onDelete,
    onClear,
    onPrint,
    onExport,
  } = props;

  const { updateParams } = useQueryParams();
  const canManageCertifications = useCanManageCertifications();

  function renderMoreContent(close: () => void) {
    const executeWithClose = (fn: () => void) => () => {
      fn();
      close();
    };

    return (
      <ButtonList>
        <Button onClick={executeWithClose(onExport)}>{t('Export')}</Button>
      </ButtonList>
    );
  }

  const handleTabSelection = (tab: CertificationStatusReportTabType) => {
    updateParams({ [QueryParam.CERTIFICATION_REPORT_TYPE]: tab });
    onTabSelect(tab);
  };

  const tabs: IActionHeaderTabData<CertificationStatusReportTabType>[] = [
    { label: t('Active'), displayValue: '', tabValue: CertificationStatusReportTabType.ACTIVE },
    { label: t('Expired'), displayValue: '', tabValue: CertificationStatusReportTabType.EXPIRED },
  ];

  function renderActionBar() {
    return (
      <CertificationStatusActionBar checkedIds={checkedIds} onDelete={onDelete} onClear={onClear} onPrint={onPrint} />
    );
  }

  const classes = classNames('certification-status-report-tab-header', className);

  return (
    <>
      <Header className={classes} actionBar={renderActionBar()}>
        <Tray spacing={1}>
          <TabBar tabs={tabs} selectedTab={selectedTab} onTabSelection={handleTabSelection} />
        </Tray>
        <Tray align="right">
          {canManageCertifications && (
            <>
              <Button type="secondary" onClick={onNewRecordClick}>
                {t('New Record')}
              </Button>
              <Button type="secondary" onClick={handleManageCertificationsButtonClick}>
                {t('Certifications')}
              </Button>
            </>
          )}
          <IconButton svg={ColumnIcon} onClick={handleColumnButtonClick} aria-label={t('Manage Columns')} />
          <MoreButton position={Position.BOTTOM_END} size={Size.MEDIUM} renderContent={renderMoreContent} />
        </Tray>
      </Header>
    </>
  );
};

export default CertificationStatusReportTabHeader;
